import { RectangleToRectangle } from "./Intersects";

export class Rectangle {
    get x(): number { return this._x; }
    set x(val: number) { this._x = val; }
    private _x: number;

    get y(): number { return this._y; }
    set y(val: number) { this._y = val; }
    private _y: number;

    get width(): number { return this._w; }
    set width(val: number) { this._w = val; }
    private _w: number;

    get height(): number { return this._h; }
    set height(val: number) { this._h = val; }
    private _h: number;

    /**
     * The x coordinate of the left of the Rectangle.
     * Changing the left property of a Rectangle object has no effect on the y and height properties. However it does affect the width property, whereas changing the x value does not affect the width property.
     */
    get left(): number { return this._x; }
    set left(val: number) {
        if (val >= this.right) {
            this._w = 0;
        }
        else {
            this._w = this.right - val;
        }

        this._x = val;
    }

    /**
     * The sum of the x and width properties.
     * Changing the right property of a Rectangle object has no effect on the x, y and height properties, however it does affect the width property.
     */
    get right(): number { return this._x + this._w; }
    set right(val: number) {
        if (val <= this._x) {
            this._w = 0;
        }
        else {
            this._w = val - this._x;
        }
    }

    /**
     * The y coordinate of the top of the Rectangle. Changing the top property of a Rectangle object has no effect on the x and width properties.
     * However it does affect the height property, whereas changing the y value does not affect the height property.
     */
    get top(): number { return this._y; }
    set top(val: number) {
        if (val >= this.bottom) {
            this._h = 0;
        }
        else {
            this._h = (this.bottom - val);
        }

        this._y = val;
    }

    /**
     * The sum of the y and height properties.
     * Changing the bottom property of a Rectangle object has no effect on the x, y and width properties, but does change the height property.
     */
    get bottom(): number { return this._y + this._h; }
    set bottom(val: number) {
        if (val <= this._y) {
            this._h = 0;
        }
        else {
            this._h = val - this._y;
        }
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor(x: number = 0, y: number = 0, w: number = 0, h: number = 0) {
        this._x = x;
        this._y = y;
        this._w = w;
        this._h = h;
    }

    //-------------------------------------------------------------------------
    /**
     * Sets the position, width, and height of the Rectangle.
     * @param x 
     * @param y 
     * @param w 
     * @param h 
     */
    //-------------------------------------------------------------------------
    setTo(x: number, y: number, w: number, h: number): Rectangle {
        this._x = x;
        this._y = y;
        this._w = w;
        this._h = h;

        return this;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    setFrom(rc: Rectangle): Rectangle {
        this._x = rc._x;
        this._y = rc._y;
        this._w = rc._w;
        this._h = rc._h;

        return this;
    }

    //-------------------------------------------------------------------------
    /**
     * Resets the position, width, and height of the Rectangle to 0.
     */
    //-------------------------------------------------------------------------
    setEmpty(): Rectangle {
        this.setTo(0, 0, 0, 0);
        return this;
    }

    //-------------------------------------------------------------------------
    /**
     * Sets the position of the Rectangle.
     * @param x 
     * @param y 
     */
    //-------------------------------------------------------------------------
    setPosition(x: number, y: number): Rectangle {
        this._x = x;
        this._y = y;

        return this;
    }

    //-------------------------------------------------------------------------
    /**
     * Determines if the Rectangle is empty. A Rectangle is empty if its width or height is less than or equal to 0.
     */
    //-------------------------------------------------------------------------
    isEmpty(): boolean {
        return (this.width <= 0 || this.height <= 0);
    }

    //-------------------------------------------------------------------------
    /**
     * Checks if the given point is inside the Rectangle's bounds.
     * @param x 
     * @param y 
     */
    //-------------------------------------------------------------------------
    contains(x: number, y: number): boolean {
        return Rectangle.Contains(this, x, y);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    move(x: number, y: number): Rectangle {
        this._x += x;
        this._y += y;

        return this;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    intersect(rect: Rectangle): boolean {
        return RectangleToRectangle(this, rect);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    intersection(rect: Rectangle, out?: Rectangle): Rectangle {
        return Rectangle.Intersection(this, rect, out);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    static Contains(rect: Rectangle, x: number, y: number): boolean {
        if (rect._w <= 0 || rect._h <= 0)
            return false;

        return (rect._x <= x && rect._x + rect._w >= x && rect._y <= y && rect._y + rect._h >= y);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    static Intersection(rectA: Rectangle, rectB: Rectangle, out: Rectangle = new Rectangle()): Rectangle {
        if (RectangleToRectangle(rectA, rectB)) {
            out.x = Math.max(rectA.x, rectB.x);
            out.y = Math.max(rectA.y, rectB.y);
            out.width = Math.min(rectA.right, rectB.right) - out.x;
            out.height = Math.min(rectA.bottom, rectB.bottom) - out.y;
        }
        else {
            out.setEmpty();
        }

        return out;
    }
}